<script setup>
import { railsApi } from '@/utils/railsApi'
import { formatCurrency } from '@core/utils/formatters'
import StatusChip from '@/components/shared/StatusChip.vue'

definePage({
  meta: {
    adminArea: true,
  },
})

const { t, te } = useI18n()
const route = useRoute()
const router = useRouter()

const loading = ref(true)
const error = ref('')
const notice = ref('')
const trainer = ref(null)
const supportCounts = ref(null)
const platformSubscription = ref(null)
const platformInvoices = ref([])
const auditLogs = ref([])

const standingTarget = ref('')

const dialog = ref({
  open: false,
  action: null,
  title: '',
  reason: '',
  toState: '',
  currentPassword: '',
  submitting: false,
})

const subscriptionStates = computed(() => [
  { title: t('admin.lifecycle.trial'), value: 'trial' },
  { title: t('admin.lifecycle.active'), value: 'active' },
  { title: t('admin.lifecycle.past_due'), value: 'past_due' },
  { title: t('admin.lifecycle.suspended'), value: 'suspended' },
  { title: t('admin.lifecycle.cancelled'), value: 'cancelled' },
])

const statusColor = state => {
  switch (state) {
  case 'active':
  case 'trial':
    return 'success'
  case 'suspended':
  case 'past_due':
    return 'warning'
  case 'deactivated':
  case 'cancelled':
    return 'secondary'
  case 'onboarding':
    return 'info'
  default:
    return 'default'
  }
}

const lifecycleLabel = state => {
  const key = `admin.lifecycle.${state}`

  return te(key) ? t(key) : (state || t('admin.lifecycle.unknown'))
}

const actionLabel = action => {
  const key = `admin.actions.${action}`

  return te(key) ? t(key) : action
}

const formatWhen = value => {
  if (!value)
    return '—'

  return new Date(value).toLocaleString('nl-NL')
}

const formatDate = value => {
  if (!value)
    return '—'

  return new Date(value).toLocaleDateString('nl-NL')
}

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    const data = await railsApi(`/api/v1/admin/trainers/${route.params.id}`)

    trainer.value = data.trainer
    supportCounts.value = data.support_counts
    platformSubscription.value = data.platform_subscription
    platformInvoices.value = data.platform_invoices || []
    auditLogs.value = data.audit_logs || []
  }
  catch (err) {
    error.value = err.payload?.error || err.message || t('admin.trainerDetail.loadError')
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

const openAction = (action, title, toState = '') => {
  dialog.value = {
    open: true,
    action,
    title,
    reason: '',
    toState,
    currentPassword: '',
    submitting: false,
  }
}

const canSubmitAction = computed(() =>
  !!dialog.value.reason.trim()
  && (dialog.value.action !== 'deactivate' || !!dialog.value.currentPassword),
)

const submitAction = async () => {
  if (!canSubmitAction.value)
    return

  dialog.value.submitting = true
  notice.value = ''
  error.value = ''
  try {
    const id = route.params.id
    let path = ''
    const body = { reason: dialog.value.reason.trim() }

    if (dialog.value.action === 'suspend')
      path = `/api/v1/admin/trainers/${id}/suspend`
    else if (dialog.value.action === 'reactivate')
      path = `/api/v1/admin/trainers/${id}/reactivate`
    else if (dialog.value.action === 'deactivate') {
      path = `/api/v1/admin/trainers/${id}/deactivate`
      body.current_password = dialog.value.currentPassword
    }
    else if (dialog.value.action === 'standing') {
      path = `/api/v1/admin/trainers/${id}/change_platform_subscription_standing`
      body.to_state = dialog.value.toState
    }

    await railsApi(path, { method: 'POST', body })
    dialog.value.open = false
    notice.value = t('admin.trainerDetail.actionSuccess')
    await load()
  }
  catch (err) {
    error.value = err.payload?.errors?.join(' ') || err.payload?.error || err.message || t('admin.trainerDetail.actionError')
  }
  finally {
    dialog.value.submitting = false
  }
}
</script>

<template>
  <div>
    <VBtn
      variant="text"
      class="mb-2 px-0"
      :to="{ name: 'admin-trainers' }"
    >
      {{ $t('admin.trainerDetail.back') }}
    </VBtn>

    <h1 class="text-h4 mb-4">
      {{ $t('admin.trainerDetail.title') }}
    </h1>

    <VAlert
      v-if="notice"
      type="success"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ notice }}
    </VAlert>
    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ error }}
    </VAlert>

    <VProgressLinear
      v-if="loading"
      indeterminate
      class="mb-4"
    />

    <template v-if="trainer">
      <VCard class="mb-4">
        <VCardText>
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <div class="text-subtitle-2 text-medium-emphasis">
                {{ $t('admin.trainerDetail.email') }}
              </div>
              <div class="mb-3">
                {{ trainer.email }}
              </div>
              <div class="text-subtitle-2 text-medium-emphasis">
                {{ $t('admin.trainerDetail.name') }}
              </div>
              <div class="mb-3">
                {{ trainer.name || '—' }}
              </div>
              <div class="text-subtitle-2 text-medium-emphasis">
                {{ $t('admin.trainerDetail.business') }}
              </div>
              <div class="mb-3">
                {{ trainer.business_name || '—' }}
              </div>
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <div class="text-subtitle-2 text-medium-emphasis">
                {{ $t('admin.trainerDetail.lifecycle') }}
              </div>
              <div class="mb-3">
                <VChip
                  size="small"
                  :color="statusColor(trainer.lifecycle_state)"
                  variant="tonal"
                >
                  {{ lifecycleLabel(trainer.lifecycle_state) }}
                </VChip>
              </div>
              <div class="text-subtitle-2 text-medium-emphasis">
                {{ $t('admin.trainerDetail.confirmed') }}
              </div>
              <div class="mb-3">
                {{ trainer.confirmed ? $t('admin.trainerDetail.yes') : $t('admin.trainerDetail.no') }}
              </div>
              <div class="text-subtitle-2 text-medium-emphasis">
                {{ $t('admin.trainerDetail.timezoneLocale') }}
              </div>
              <div>{{ trainer.timezone || '—' }} / {{ trainer.locale || '—' }}</div>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>

      <VCard class="mb-4">
        <VCardItem>
          <VCardTitle>{{ $t('admin.trainerDetail.supportTitle') }}</VCardTitle>
          <VCardSubtitle>{{ $t('admin.trainerDetail.supportSubtitle') }}</VCardSubtitle>
        </VCardItem>
        <VCardText v-if="supportCounts">
          <div>{{ $t('admin.trainerDetail.clients') }}: <strong>{{ supportCounts.clients }}</strong></div>
          <div>{{ $t('admin.trainerDetail.openRelationships') }}: <strong>{{ supportCounts.open_coaching_relationships }}</strong></div>
          <div>{{ $t('admin.trainerDetail.occupyingAppointments') }}: <strong>{{ supportCounts.occupying_appointments }}</strong></div>
        </VCardText>
      </VCard>

      <VCard class="mb-4">
        <VCardItem>
          <VCardTitle>{{ $t('admin.trainerDetail.subscriptionTitle') }}</VCardTitle>
        </VCardItem>
        <VCardText>
          <template v-if="platformSubscription">
            <div class="mb-2">
              {{ $t('admin.trainerDetail.standing') }}:
              <VChip
                size="small"
                :color="statusColor(platformSubscription.lifecycle_state)"
                variant="tonal"
              >
                {{ lifecycleLabel(platformSubscription.lifecycle_state) }}
              </VChip>
            </div>
            <div class="mb-4 text-medium-emphasis">
              {{ $t('admin.trainerDetail.plan') }}: {{ platformSubscription.plan_name }}
            </div>
            <VSelect
              v-model="standingTarget"
              :items="subscriptionStates"
              item-title="title"
              item-value="value"
              :label="$t('admin.trainerDetail.newStanding')"
              class="mb-3"
              style="max-width: 20rem;"
            />
            <VBtn
              color="warning"
              :disabled="!standingTarget || standingTarget === platformSubscription.lifecycle_state"
              @click="openAction('standing', $t('admin.trainerDetail.changeStandingTitle'), standingTarget)"
            >
              {{ $t('admin.trainerDetail.changeStanding') }}
            </VBtn>
          </template>
          <template v-else>
            <p class="text-medium-emphasis">
              {{ $t('admin.trainerDetail.noSubscription') }}
            </p>
            <VSelect
              v-model="standingTarget"
              :items="subscriptionStates"
              item-title="title"
              item-value="value"
              :label="$t('admin.trainerDetail.standingLabel')"
              class="mb-3"
              style="max-width: 20rem;"
            />
            <VBtn
              color="warning"
              :disabled="!standingTarget"
              @click="openAction('standing', $t('admin.trainerDetail.setStandingTitle'), standingTarget)"
            >
              {{ $t('admin.trainerDetail.setStanding') }}
            </VBtn>
          </template>
        </VCardText>
      </VCard>

      <VCard class="mb-4">
        <VCardItem>
          <VCardTitle>{{ $t('admin.trainerDetail.billingTitle') }}</VCardTitle>
        </VCardItem>
        <VCardText>
          <VAlert
            v-if="!platformInvoices.length"
            type="info"
            variant="tonal"
            role="status"
          >
            {{ $t('admin.trainerDetail.billingEmpty') }}
          </VAlert>
          <VTable
            v-else
            density="comfortable"
          >
            <thead>
              <tr>
                <th scope="col">
                  {{ $t('admin.invoices.columns.number') }}
                </th>
                <th scope="col">
                  {{ $t('admin.invoices.columns.amount') }}
                </th>
                <th scope="col">
                  {{ $t('admin.invoices.columns.due') }}
                </th>
                <th scope="col">
                  {{ $t('admin.invoices.columns.status') }}
                </th>
                <th scope="col">
                  {{ $t('admin.columns.action') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="invoice in platformInvoices"
                :key="invoice.id"
              >
                <td>{{ invoice.invoice_number }}</td>
                <td>{{ formatCurrency(invoice.amount_cents, invoice.currency) }}</td>
                <td>{{ formatDate(invoice.due_on) }}</td>
                <td>
                  <StatusChip
                    domain="platform_invoice"
                    :state="invoice.lifecycle_state"
                  />
                </td>
                <td>
                  <VBtn
                    size="small"
                    variant="text"
                    @click="router.push({ name: 'admin-invoices-id', params: { id: invoice.id } })"
                  >
                    {{ $t('admin.trainerDetail.viewInvoice') }}
                  </VBtn>
                </td>
              </tr>
            </tbody>
          </VTable>
        </VCardText>
      </VCard>

      <VCard class="mb-4">
        <VCardItem>
          <VCardTitle>{{ $t('admin.trainerDetail.accountActions') }}</VCardTitle>
        </VCardItem>
        <VCardText class="d-flex flex-wrap gap-3">
          <VBtn
            v-if="trainer.lifecycle_state === 'active'"
            color="warning"
            @click="openAction('suspend', $t('admin.trainerDetail.suspendTitle'))"
          >
            {{ $t('admin.trainerDetail.suspend') }}
          </VBtn>
          <VBtn
            v-if="trainer.lifecycle_state === 'suspended'"
            color="primary"
            @click="openAction('reactivate', $t('admin.trainerDetail.reactivateTitle'))"
          >
            {{ $t('admin.trainerDetail.reactivate') }}
          </VBtn>
          <VBtn
            v-if="trainer.lifecycle_state === 'active' || trainer.lifecycle_state === 'suspended'"
            color="error"
            @click="openAction('deactivate', $t('admin.trainerDetail.deactivateTitle'))"
          >
            {{ $t('admin.trainerDetail.deactivate') }}
          </VBtn>
          <span
            v-if="trainer.lifecycle_state === 'deactivated' || trainer.lifecycle_state === 'onboarding'"
            class="text-medium-emphasis"
          >
            {{ $t('admin.trainerDetail.noActions', { state: lifecycleLabel(trainer.lifecycle_state) }) }}
          </span>
        </VCardText>
      </VCard>

      <VCard>
        <VCardItem>
          <VCardTitle>{{ $t('admin.trainerDetail.auditTitle') }}</VCardTitle>
          <template #append>
            <VBtn
              variant="text"
              size="small"
              :to="{ name: 'admin-audit-logs', query: { trainer_id: trainer.id } }"
            >
              {{ $t('admin.trainerDetail.viewGlobalLog') }}
            </VBtn>
          </template>
        </VCardItem>
        <VCardText>
          <p
            v-if="!auditLogs.length"
            class="text-medium-emphasis mb-0"
          >
            {{ $t('admin.trainerDetail.emptyAudit') }}
          </p>
          <VTable v-else>
            <thead>
              <tr>
                <th scope="col">
                  {{ $t('admin.columns.when') }}
                </th>
                <th scope="col">
                  {{ $t('admin.columns.who') }}
                </th>
                <th scope="col">
                  {{ $t('admin.columns.action') }}
                </th>
                <th scope="col">
                  {{ $t('admin.columns.reason') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="log in auditLogs"
                :key="log.id"
              >
                <td>{{ formatWhen(log.created_at) }}</td>
                <td>{{ log.platform_admin?.email }}</td>
                <td>{{ actionLabel(log.action) }}</td>
                <td>{{ log.reason }}</td>
              </tr>
            </tbody>
          </VTable>
        </VCardText>
      </VCard>
    </template>

    <VDialog
      v-model="dialog.open"
      max-width="520"
    >
      <VCard>
        <VCardTitle>{{ dialog.title }}</VCardTitle>
        <VCardText>
          <p class="mb-4">
            {{ $t('admin.trainerDetail.dialogReasonHelp') }}
          </p>
          <VTextarea
            v-model="dialog.reason"
            :label="$t('admin.trainerDetail.reasonLabel')"
            rows="3"
            maxlength="500"
            autofocus
          />
          <VTextField
            v-if="dialog.action === 'deactivate'"
            v-model="dialog.currentPassword"
            type="password"
            autocomplete="current-password"
            class="mt-4"
            :label="$t('admin.mollieConnection.currentPasswordLabel')"
            :hint="$t('admin.mollieConnection.currentPasswordHint')"
            persistent-hint
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="dialog.open = false"
          >
            {{ $t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :disabled="!canSubmitAction"
            :loading="dialog.submitting"
            @click="submitAction"
          >
            {{ $t('admin.trainerDetail.confirm') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
